// TODUE V2 — SQLite DDL + seed (idempotentne).
// SCHEMA_VERSION = 15 (6 = pôvodné Dexie verno; 7 = +obligation_state, C-3;
// 8 = +recommendation_state + advisor_log + prediction_log — advisor scaffold;
// 9 = +records.paid/paid_at; 10 = +decision_events — Attention OS denník;
// 11 = +decision_events.context_bands; 12 = +commitments — iba user-confirmed
// zmluvné/opakované fakty, nie kópia zákonných pravidiel;
// 13 = +collaborator_tasks — lokálny owner-side register capability linkov;
// 14 = +sync_outbox + sync_conflicts — lokálna odolnosť Drive syncu;
// 15 = +flow_runs — live zákazky (zakazky engine);
// 16 = +vehicles + vehicle_trips (projekcia) + vehicle_trip_ledger — kniha jázd:
//   vehicles/vehicle_trips sú bežné synced stores; vehicle_trip_ledger je
//   LOCAL-ONLY append-only hash-chain (dôkaz pred daňovým úradom) — mimo
//   SYNC_STORE_CONFIG rovnako ako decision_events (bloker B1 pagination; sync
//   ledgera príde ako samostatný segmentovaný task). Spec: MasterProject
//   feature-ideas/ideas/todue-kniha-jazd-SPEC-v2.md §3.
// Ide do backup.schema_version. Greenfield → finálna schéma rovno, žiadne
// version upgrades (CREATE TABLE IF NOT EXISTS pridá novú tabuľku aj na starej DB).
//
// Kernel markery (schema_version, obligation_pack_version) sú stĺpce s DEFAULT
// (nie migrácia). Soft-delete `deleted_at` + LWW (rev/updated_at/created_at) ako
// obyčajné stĺpce — merge ostáva v app vrstve. JSON polia sú TEXT
// (stringify/parse rieši adapter.js).
//
// Spec: MasterProject/workflows/todue-v2-mythos/SQLITE-MIGRATION-SPEC.md §3

// 17 = +vehicle_costs (projekcia nákladov na vozidlo, synced) — kniha jázd §85n ods. 6 e:
//   evidencia VŠETKÝCH nákladov na prevádzku (nielen PHM). Autoritatívny dôkaz ostáva
//   vehicle_trip_ledger (cost_recorded/cost_voided/cost_corrected). Spec §2 naklad_vozidla.
// 18 = collaborator_tasks teraz cross-device (title/due_date/status v SYNC_STORE_CONFIG) —
//   `capability_token`/`invite_url` (surový secretbox kľúč) presunuté do NOVEJ LOCAL-ONLY
//   tabuľky `collaborator_task_secrets` (1:1 na id), mimo SYNC_STORE_CONFIG zámerne. Naivné
//   pridanie celého pôvodného riadku by poslalo dešifrovací kľúč na Drive v čistom texte —
//   rovnaká trieda úniku ako 2026-07-26 backup incident, tentokrát cez sync-push namiesto
//   zálohy. Delegovaná úloha je teraz vidno na všetkých zariadeniach, no capability link
//   (a teda schopnosť znova zdieľať/dešifrovať) ostáva viazaný na zariadenie kde vznikol.
export const SCHEMA_VERSION = 18;

// Jediný číselník storov synchronizovaných na Drive. Schéma ho používa na
// trigger-outbox a driveSync ho re-exportuje ako svoj verejný kontrakt.
// Lokálne tabuľky (logy, conflicts, outbox) sem nikdy nepatria.
export const SYNC_STORE_CONFIG = Object.freeze([
  { store: 'records', prefix: 'record-', pk: 'id' },
  { store: 'categories', prefix: 'category-', pk: 'id' },
  { store: 'category_rules', prefix: 'catrule-', pk: 'id' },
  { store: 'vendors', prefix: 'vendor-', pk: 'ico' },
  { store: 'settings', prefix: 'setting-', pk: 'key' },
  { store: 'counterparties', prefix: 'counterparty-', pk: 'id' },
  { store: 'bank_transactions', prefix: 'banktx-', pk: 'id' },
  { store: 'containers', prefix: 'container-', pk: 'id' },
  { store: 'obligation_state', prefix: 'obstate-', pk: 'obligation_id' },
  { store: 'recommendation_state', prefix: 'recstate-', pk: 'recommendation_id' },
  { store: 'commitments', prefix: 'commitment-', pk: 'id' },
  { store: 'flow_runs', prefix: 'flowrun-', pk: 'id' },
  { store: 'vehicles', prefix: 'vehicle-', pk: 'id' },
  { store: 'vehicle_trips', prefix: 'vehicletrip-', pk: 'id' },
  { store: 'vehicle_costs', prefix: 'vehiclecost-', pk: 'id' },
  // collaborator_tasks: LEN title/due_date/status (žiadny secret stĺpec ostal na tomto
  // riadku, viď collaborator_task_secrets nižšie) — full-row `.put()` ako vehicles*, preto
  // NEPATRÍ do HLC_STORES (rovnaké odôvodnenie ako vehicles/vehicle_trips/vehicle_costs).
  { store: 'collaborator_tasks', prefix: 'collabtask-', pk: 'id' },
]);

// Stores s per-field CRDT `_hlc` mintom + merge (sync hardening Fáza 2). JEDINÝ zdroj pravdy —
// adapter (mint na add/update) aj driveSync (merge/observe/push gate) ho importujú, aby sa dva
// ručne udržiavané zoznamy nerozišli. Každý store tu MUSÍ mať full LWW triplet + kernel markery
// + `_hlc TEXT` stĺpec (DDL + migrácia nižšie). Rozšírenie z records na counterparties/
// bank_transactions/containers (vlna 2). bank_transactions je money-path → cross-family verify.
// vlna 3 (2026-07-27, Fable decider A+B, oklieštené cross-family): +commitments (píše cez `.add` →
// adapter stampuje `_hlc` → per-field CRDT funguje). vehicles/vehicle_trips/vehicle_costs SEM NEPATRIA:
// píšu full-row `.put()` (nestampovaný) + projekčný rewrite modelu → stampUpdate by re-stampol VŠETKY
// polia = degradácia na whole-row LWW, žiadny per-field benefit (Codex+DeepSeek verify). Ich CRDT
// vyžaduje value-diff stamping na put + overenie sync-apply → PENDING-sync.md P1-followup. Ich
// divergent-rev stratu medzitým kryje B poistka (divergentLoser, obnoviteľná do sync_conflicts).
export const HLC_STORES = new Set([
  'records', 'counterparties', 'bank_transactions', 'containers', 'commitments',
]);

// CLOUD-safe allowlist — JEDINÉ stores, ktoré smú ísť do Drive snapshotu. Fail-CLOSED:
// čokoľvek mimo SYNC_STORE_CONFIG (decision_events, collaborator_task_secrets.capability_token,
// *_log, embeddings, vehicle_trip_ledger, sync_conflicts…) sa NIKDY nenahrá na Drive.
// Denylist bola fail-open (míňala stores) — cross-family verify 2026-07-26 (Codex).
// `buildBackup({cloudSafe:true})` (Drive cesta) filtruje týmto; user-download ostáva úplný.
export const CLOUD_SNAPSHOT_STORES = new Set(SYNC_STORE_CONFIG.map((c) => c.store));

// ─── Seed dáta (náhrada Dexie db.on('populate')) ─────────────────────────────
// Re-exportujú sa aj z db.js (testy ich importujú) — tu sú zdroj pravdy pre seed.
export const SEED_CATEGORIES = [
  { id: 'potraviny',     name_sk: 'Potraviny',       tax_relevant: true,  sort_order: 1 },
  { id: 'pohonne_hmoty', name_sk: 'Pohonné hmoty',   tax_relevant: true,  sort_order: 2 },
  { id: 'telekom',       name_sk: 'Telekomunikácie', tax_relevant: true,  sort_order: 3 },
  { id: 'kancelaria',    name_sk: 'Kancelária',      tax_relevant: true,  sort_order: 4 },
  { id: 'sluzby',        name_sk: 'Služby',          tax_relevant: true,  sort_order: 5 },
  { id: 'ostatne',       name_sk: 'Ostatné',         tax_relevant: false, sort_order: 6 },
];

export const SEED_CATEGORY_RULES = [
  { id: 'rule_slovnaft', match_type: 'name_contains', match_value: 'slovnaft', category_id: 'pohonne_hmoty', priority: 1 },
  { id: 'rule_omv',      match_type: 'name_contains', match_value: 'omv',      category_id: 'pohonne_hmoty', priority: 1 },
  { id: 'rule_o2',       match_type: 'name_contains', match_value: 'o2',       category_id: 'telekom',       priority: 2 },
  { id: 'rule_telekom',  match_type: 'name_contains', match_value: 'telekom',  category_id: 'telekom',       priority: 2 },
  { id: 'rule_lidl',     match_type: 'name_contains', match_value: 'lidl',     category_id: 'potraviny',     priority: 3 },
];

// ─── DDL — 20 tabuliek + indexy (okrem idx_records_document_source_unique,
//     ktorý sa vytvára samostatne po reconcile v applySchema) ───────────────
// CREATE TABLE IF NOT EXISTS / CREATE INDEX IF NOT EXISTS → applySchema idempotentné.
export const DDL = `
-- 1) records (PK id; JSON: items, counterparty, seller, handshake, custom, vat, einvoice, ocr_raw)
CREATE TABLE IF NOT EXISTS records (
  id TEXT PRIMARY KEY,
  created_at TEXT, updated_at TEXT, rev INTEGER NOT NULL DEFAULT 1,
  deleted_at TEXT, schema_version INTEGER NOT NULL DEFAULT 1, obligation_pack_version INTEGER NOT NULL DEFAULT 0,
  module TEXT, entry_date TEXT, tax_date TEXT, issue_date TEXT, due_date TEXT, due TEXT,
  payment_date TEXT, number TEXT, label TEXT,
  counterparty_name TEXT, counterparty_ico TEXT, counterparty_dic TEXT, counterparty_icdph TEXT,
  doc_number TEXT, amount_cents INTEGER, subtotal_cents INTEGER, vat_total_cents INTEGER,
  vat_amount_cents INTEGER, vat_rate INTEGER, currency TEXT DEFAULT 'EUR',
  qty REAL, unit TEXT, unit_cost_cents INTEGER, income_type TEXT, variable_symbol TEXT, iban TEXT,
  document_type TEXT, direction TEXT, paid_via TEXT,
  paid INTEGER, paid_at TEXT,
  category_id TEXT, category_src TEXT, client_id TEXT, container_id TEXT,
  status TEXT, source TEXT, attachment_ref TEXT, extraction_source TEXT, extraction_confidence REAL,
  items TEXT, counterparty TEXT, seller TEXT, handshake TEXT, custom TEXT, vat TEXT, einvoice TEXT, ocr_raw TEXT,
  _hlc TEXT
);
CREATE INDEX IF NOT EXISTS idx_records_module ON records(module);
CREATE INDEX IF NOT EXISTS idx_records_entry_date ON records(entry_date);
CREATE INDEX IF NOT EXISTS idx_records_cp_ico ON records(counterparty_ico);
CREATE INDEX IF NOT EXISTS idx_records_deleted_at ON records(deleted_at);
CREATE INDEX IF NOT EXISTS idx_records_container_id ON records(container_id);

-- 2) categories (PK id slug; updated_at only — NO rev/created_at/deleted_at)
CREATE TABLE IF NOT EXISTS categories (
  id TEXT PRIMARY KEY, name_sk TEXT, tax_relevant INTEGER, sort_order INTEGER, updated_at TEXT
);
CREATE INDEX IF NOT EXISTS idx_categories_sort ON categories(sort_order);

-- 3) category_rules (PK id; NO updated_at)
CREATE TABLE IF NOT EXISTS category_rules (
  id TEXT PRIMARY KEY, match_type TEXT, match_value TEXT, category_id TEXT, priority INTEGER
);
CREATE INDEX IF NOT EXISTS idx_rules_match_type ON category_rules(match_type);
CREATE INDEX IF NOT EXISTS idx_rules_priority ON category_rules(priority);

-- 4) vendors (PK ico — string, NOT UUID; updated_at only)
CREATE TABLE IF NOT EXISTS vendors (
  ico TEXT PRIMARY KEY, name_normalized TEXT, default_category_id TEXT,
  confirm_count INTEGER, prev_category_id TEXT, prev_confirm_count INTEGER,
  last_used_at TEXT, updated_at TEXT
);
CREATE INDEX IF NOT EXISTS idx_vendors_name_norm ON vendors(name_normalized);

-- 5) settings (PK key; value POLYMORFNÉ + tokens/pub/sec → JSON TEXT; updated_at always)
CREATE TABLE IF NOT EXISTS settings (
  key TEXT PRIMARY KEY, value TEXT, tokens TEXT, pub TEXT, sec TEXT, updated_at TEXT
);

-- 6) counterparties (PK id UUID; full LWW triplet + kernel markers)
CREATE TABLE IF NOT EXISTS counterparties (
  id TEXT PRIMARY KEY,
  created_at TEXT, updated_at TEXT, rev INTEGER NOT NULL DEFAULT 1,
  deleted_at TEXT, schema_version INTEGER NOT NULL DEFAULT 1, obligation_pack_version INTEGER NOT NULL DEFAULT 0,
  name TEXT, name_normalized TEXT, ico TEXT DEFAULT '', dic TEXT DEFAULT '', ic_dph TEXT DEFAULT '',
  address TEXT DEFAULT '', email TEXT DEFAULT '', phone TEXT DEFAULT '', type TEXT,
  use_count INTEGER DEFAULT 0, last_used_at TEXT,
  _hlc TEXT
);
CREATE INDEX IF NOT EXISTS idx_cp_ico ON counterparties(ico);
CREATE INDEX IF NOT EXISTS idx_cp_name_norm ON counterparties(name_normalized);
CREATE INDEX IF NOT EXISTS idx_cp_deleted_at ON counterparties(deleted_at);

-- 7) bank_transactions (PK id UUID; JSON: raw)
CREATE TABLE IF NOT EXISTS bank_transactions (
  id TEXT PRIMARY KEY,
  created_at TEXT, updated_at TEXT, rev INTEGER NOT NULL DEFAULT 1,
  deleted_at TEXT, schema_version INTEGER NOT NULL DEFAULT 1, obligation_pack_version INTEGER NOT NULL DEFAULT 0,
  tx_ref TEXT, value_date TEXT, amount_cents INTEGER, variable_symbol TEXT, counterparty_name TEXT,
  matched_record_id TEXT, match_kind TEXT DEFAULT 'exact', import_source TEXT DEFAULT 'csv',
  raw TEXT,
  _hlc TEXT
);
CREATE INDEX IF NOT EXISTS idx_bt_tx_ref ON bank_transactions(tx_ref);
CREATE INDEX IF NOT EXISTS idx_bt_value_date ON bank_transactions(value_date);
CREATE INDEX IF NOT EXISTS idx_bt_matched ON bank_transactions(matched_record_id);
CREATE INDEX IF NOT EXISTS idx_bt_deleted_at ON bank_transactions(deleted_at);

-- 8) containers (PK id UUID; self-ref hierarchy)
CREATE TABLE IF NOT EXISTS containers (
  id TEXT PRIMARY KEY,
  created_at TEXT, updated_at TEXT, rev INTEGER NOT NULL DEFAULT 1,
  deleted_at TEXT, schema_version INTEGER NOT NULL DEFAULT 1, obligation_pack_version INTEGER NOT NULL DEFAULT 0,
  parent_id TEXT, module_type TEXT, label TEXT DEFAULT '',
  _hlc TEXT
);
CREATE INDEX IF NOT EXISTS idx_containers_parent ON containers(parent_id);
CREATE INDEX IF NOT EXISTS idx_containers_module_type ON containers(module_type);
CREATE INDEX IF NOT EXISTS idx_containers_deleted_at ON containers(deleted_at);

-- 9) obligation_state (PK obligation_id = C-2 deterministic hash, TEXT — NIE UUID).
--    JEDINÁ perzistovaná časť obligations systému (C-3) — obligations samotné sú
--    vždy derived (deriveObligations), nikdy stored. state: 'done'|'dismissed'|'snoozed';
--    until = ISO dátum, len pre snoozed. Full LWW triplet + kernel markery.
CREATE TABLE IF NOT EXISTS obligation_state (
  obligation_id TEXT PRIMARY KEY,
  created_at TEXT, updated_at TEXT, rev INTEGER NOT NULL DEFAULT 1,
  deleted_at TEXT, schema_version INTEGER NOT NULL DEFAULT 1,
  obligation_pack_version INTEGER NOT NULL DEFAULT 0,
  state TEXT,
  until TEXT
);
CREATE INDEX IF NOT EXISTS idx_obstate_deleted_at ON obligation_state(deleted_at);

-- 10) recommendation_state (PK recommendation_id = deterministic hash, TEXT — NIE UUID).
--     1:1 kópia obligation_state konvencií — JEDINÁ perzistovaná časť advisor
--     odporúčaní (recommendations samotné sú vždy derived, nikdy stored).
--     state: 'done'|'dismissed'|'snoozed'|'accepted'; until = ISO dátum, len pre
--     snoozed. Full LWW triplet + kernel markery → Drive sync (SYNC_STORES).
CREATE TABLE IF NOT EXISTS recommendation_state (
  recommendation_id TEXT PRIMARY KEY,
  created_at TEXT, updated_at TEXT, rev INTEGER NOT NULL DEFAULT 1,
  deleted_at TEXT, schema_version INTEGER NOT NULL DEFAULT 1,
  obligation_pack_version INTEGER NOT NULL DEFAULT 0,
  state TEXT,
  until TEXT
);
CREATE INDEX IF NOT EXISTS idx_recstate_deleted_at ON recommendation_state(deleted_at);

-- 11) advisor_log (LOCAL-ONLY append log, light vzor category_rules — BEZ
--     rev/deleted_at; NIKDY v SYNC_STORES). event = shown/clicked/dismissed…
CREATE TABLE IF NOT EXISTS advisor_log (
  id TEXT PRIMARY KEY, at TEXT, rule_id TEXT, event TEXT, created_at TEXT
);
CREATE INDEX IF NOT EXISTS idx_advisorlog_at ON advisor_log(at);

-- 12) prediction_log (LOCAL-ONLY append log, scalar-only — žiadne JSON stĺpce;
--     NIKDY v SYNC_STORES). Kalibrácia predikcií: predicted vs actual v centoch.
CREATE TABLE IF NOT EXISTS prediction_log (
  id TEXT PRIMARY KEY, kind TEXT, horizon TEXT,
  predicted_cents INTEGER, predicted_p10_cents INTEGER, predicted_p90_cents INTEGER,
  actual_cents INTEGER, target_from TEXT, target_to TEXT, at TEXT
);
CREATE INDEX IF NOT EXISTS idx_predictionlog_kind ON prediction_log(kind);

-- 13) decision_events (LOCAL-ONLY append-only denník rozhodnutí — Attention OS,
--     TODUE_DECISION_EVENTS.md §2.1; JSON: record_ids, expected). NIKDY
--     v SYNC_STORES (bloker B1: sync ťahá pageSize=1000 bez pagination —
--     append-only store by nad 1000 riadkov TICHO strácal eventy; sync príde
--     ako samostatný task so segmentáciou). ŽIADNE raw sumy — event nesie
--     record_ids + pásma, presná suma sa deriví joinom na records (K2/K8).
--     Append-only: žiadny update, žiadny prune (§6.1) — put-if-absent
--     vynucuje decisionEvents.js.
CREATE TABLE IF NOT EXISTS decision_events (
  id TEXT PRIMARY KEY,
  event_version INTEGER NOT NULL DEFAULT 2,

  decision_key TEXT NOT NULL,
  candidate_id TEXT,
  rule_id TEXT,
  pack_ref TEXT,
  subject_type TEXT,
  subject_key TEXT,
  record_ids TEXT,

  event_type TEXT NOT NULL,
  action_type TEXT,
  action_instance_id TEXT NOT NULL,
  reason_code TEXT,
  channel TEXT,

  actor_type TEXT NOT NULL,
  actor_id_hash TEXT,
  actor_role TEXT,
  owner_role TEXT,
  delegate_target_hash TEXT,

  priority_class TEXT,
  confidence_band TEXT,
  evidence_mode TEXT,
  impact_band TEXT,
  context_bands TEXT,

  expected TEXT,
  check_at TEXT,
  input_hash TEXT,

  occurred_at TEXT NOT NULL,
  device_id_hash TEXT NOT NULL,
  created_at TEXT NOT NULL
);
CREATE INDEX IF NOT EXISTS idx_de_decision ON decision_events(decision_key);
CREATE INDEX IF NOT EXISTS idx_de_rule ON decision_events(rule_id);
CREATE INDEX IF NOT EXISTS idx_de_subject ON decision_events(subject_key);
CREATE INDEX IF NOT EXISTS idx_de_occurred ON decision_events(occurred_at);
CREATE INDEX IF NOT EXISTS idx_de_action_instance ON decision_events(action_instance_id);
CREATE INDEX IF NOT EXISTS idx_de_check_at ON decision_events(check_at);

-- 14) commitments — potvrdené zmluvné/opakované fakty používateľa.
-- Zákonný text ani termín sa sem nekopíruje; legal_pack_ref/legal_rule_id sú
-- iba referencia na verziovaný pack. Bez outcome_source je výsledok unknown.
CREATE TABLE IF NOT EXISTS commitments (
  id TEXT PRIMARY KEY,
  created_at TEXT, updated_at TEXT, rev INTEGER NOT NULL DEFAULT 1,
  deleted_at TEXT, schema_version INTEGER NOT NULL DEFAULT 1,
  obligation_pack_version INTEGER NOT NULL DEFAULT 0,
  kind TEXT NOT NULL,
  label TEXT NOT NULL,
  cadence TEXT,
  day_of_month INTEGER,
  due_date TEXT,
  confirmed_at TEXT NOT NULL,
  legal_pack_ref TEXT,
  legal_rule_id TEXT,
  outcome_source TEXT,
  _hlc TEXT
);
CREATE INDEX IF NOT EXISTS idx_commitments_kind ON commitments(kind);
CREATE INDEX IF NOT EXISTS idx_commitments_deleted ON commitments(deleted_at);

-- 15) collaborator_tasks — owner-side register capability úloh. Token ani text
-- nejdú brokerovi mimo secretbox envelope. Riadok NEnesie žiadny secret stĺpec
-- (viď collaborator_task_secrets, v18) → je v SYNC_STORE_CONFIG, vidno na všetkých
-- vlastných zariadeniach. Živý broker + cross-device bezpečnosť sú verification gate K2/K4.
CREATE TABLE IF NOT EXISTS collaborator_tasks (
  id TEXT PRIMARY KEY,
  created_at TEXT, updated_at TEXT, rev INTEGER NOT NULL DEFAULT 1,
  deleted_at TEXT, schema_version INTEGER NOT NULL DEFAULT 1,
  obligation_pack_version INTEGER NOT NULL DEFAULT 0,
  title TEXT NOT NULL,
  due_date TEXT,
  status TEXT NOT NULL,
  status_observed_at TEXT
);
CREATE INDEX IF NOT EXISTS idx_collab_tasks_status ON collaborator_tasks(status);
CREATE INDEX IF NOT EXISTS idx_collab_tasks_deleted ON collaborator_tasks(deleted_at);

-- 18) collaborator_task_secrets — LOCAL-ONLY, 1:1 na collaborator_tasks.id. Nesie presne
-- to, čo NIKDY nesmie opustiť zariadenie: surový secretbox kľúč (capability_token) a odkaz
-- ktorý ho nesie vo fragmente (invite_url = .../c/#token). Zámerne mimo SYNC_STORE_CONFIG
-- aj CLOUD_SNAPSHOT_STORES (ktorý sa z neho odvodzuje) — kto má tento riadok, dešifruje
-- a ovláda danú delegovanú úlohu. Bez tejto separácie by pridanie collaborator_tasks do
-- syncu poslalo token na Drive v čistom texte (rovnaká trieda ako 2026-07-26 backup leak).
CREATE TABLE IF NOT EXISTS collaborator_task_secrets (
  id TEXT PRIMARY KEY,
  capability_token TEXT NOT NULL,
  invite_url TEXT NOT NULL
);

-- 16) flow_runs — live zákazky (zakazky engine). Full LWW triplet + kernel markery.
-- JSON: context (vystupy/stavy/porty), steps (pole krokov). SYNC_STORES → Drive.
CREATE TABLE IF NOT EXISTS flow_runs (
  id TEXT PRIMARY KEY,
  created_at TEXT, updated_at TEXT, rev INTEGER NOT NULL DEFAULT 1,
  deleted_at TEXT, schema_version INTEGER NOT NULL DEFAULT 1, obligation_pack_version INTEGER NOT NULL DEFAULT 0,
  flow_id TEXT NOT NULL,
  status TEXT NOT NULL DEFAULT 'ok',
  title TEXT NOT NULL,
  subject_type TEXT,
  subject_id TEXT,
  context TEXT NOT NULL,
  steps TEXT NOT NULL,
  current_step TEXT,
  stage TEXT,
  message TEXT,
  wait_token TEXT,
  waiting_since TEXT
);
CREATE INDEX IF NOT EXISTS idx_flow_runs_flow_id ON flow_runs(flow_id);
CREATE INDEX IF NOT EXISTS idx_flow_runs_status ON flow_runs(status);
CREATE INDEX IF NOT EXISTS idx_flow_runs_deleted_at ON flow_runs(deleted_at);

-- 17) sync_outbox — LOCAL-ONLY durable fronta. Trigger zapisuje v rovnakej
-- transakcii ako používateľský záznam; sync maže položku až po potvrdenom Drive
-- uploade. __seeded__ marker ostáva navždy a odlišuje upgrade od mock/fallbacku.
CREATE TABLE IF NOT EXISTS sync_outbox (
  id TEXT PRIMARY KEY,
  store TEXT NOT NULL,
  pk TEXT NOT NULL,
  queued_at TEXT NOT NULL
);
CREATE INDEX IF NOT EXISTS idx_sync_outbox_store ON sync_outbox(store);

-- 18) sync_conflicts — LOCAL-ONLY nemenné kópie porazených verzií. Nie je v
-- SYNC_STORE_CONFIG: conflict nesmie spustiť rekurzívny cloud sync.
CREATE TABLE IF NOT EXISTS sync_conflicts (
  id TEXT PRIMARY KEY,
  store TEXT NOT NULL,
  pk TEXT NOT NULL,
  loser_json TEXT NOT NULL,
  winner_json TEXT NOT NULL,
  reason TEXT NOT NULL,
  created_at TEXT NOT NULL
);
CREATE INDEX IF NOT EXISTS idx_sync_conflicts_record ON sync_conflicts(store, pk);

-- 19) pack_migration_sessions — LOCAL-ONLY (mimo Drive sync). NS-006 dependency migrations:
-- frozen plán + ledger aplikovaných step id + backup + report. *_json sú raw TEXT (nie JSON_COLUMNS,
-- journal ich číta/píše ako reťazce).
CREATE TABLE IF NOT EXISTS pack_migration_sessions (
  id TEXT PRIMARY KEY,
  created_at TEXT, updated_at TEXT,
  status TEXT,
  targets_json TEXT, order_json TEXT, plan_json TEXT,
  backups_json TEXT, created_ids_json TEXT, applied_steps_json TEXT,
  cursor INTEGER DEFAULT 0, report_json TEXT
);
CREATE INDEX IF NOT EXISTS idx_pack_migration_sessions_status ON pack_migration_sessions(status);

-- 20) import_sessions — LOCAL-ONLY (mimo Drive sync). NS-002 migration kernel: stavový automat
-- importu (dry_run → confirmed → committing → committed|failed|aborted) + zachovaný originál
-- (raw_file) + frozen plán + rozhodnutia usera. *_json sú raw TEXT (journal ich číta/píše ako reťazce).
CREATE TABLE IF NOT EXISTS import_sessions (
  id TEXT PRIMARY KEY,
  created_at TEXT, updated_at TEXT,
  status TEXT,
  provider TEXT, file_name TEXT, file_sha256 TEXT, file_bytes INTEGER, raw_file TEXT,
  plan_json TEXT, decisions_json TEXT,
  cursor INTEGER DEFAULT 0, report_json TEXT
);
CREATE INDEX IF NOT EXISTS idx_import_sessions_status ON import_sessions(status);

-- 21) vehicles — register vozidiel (kniha jázd). Full LWW triplet + kernel markery → Drive sync.
-- Statické polia sú stĺpce; TEMPORÁLNE stavy (dph_rezim/phm_rezim/usage/ownership/ecv s valid_from/valid_to
-- a priznak_oznamenia FS) žijú v JSON intervals stĺpci — režim vozidla sa mení v čase (review SHOULD-FIX).
-- category = M1/L1e/L3e/… (určuje či osobitný režim DPH platí). norm_consumption = normovaná spotreba z techničáku.
CREATE TABLE IF NOT EXISTS vehicles (
  id TEXT PRIMARY KEY,
  created_at TEXT, updated_at TEXT, rev INTEGER NOT NULL DEFAULT 1,
  deleted_at TEXT, schema_version INTEGER NOT NULL DEFAULT 1, obligation_pack_version INTEGER NOT NULL DEFAULT 0,
  vin TEXT, ecv TEXT, label TEXT, category TEXT, fuel_type TEXT,
  norm_consumption REAL, acquired_at TEXT,
  odo_start INTEGER, odo_start_at TEXT,
  intervals TEXT, custom TEXT
);
CREATE INDEX IF NOT EXISTS idx_vehicles_deleted_at ON vehicles(deleted_at);
CREATE INDEX IF NOT EXISTS idx_vehicles_vin ON vehicles(vin);

-- 22) vehicle_trips — PROJEKCIA jázd (UI číta odtiaľto). Autoritatívny dôkaz je vehicle_trip_ledger.
-- Full LWW triplet + kernel markery → Drive sync. seq_no = neprerušená rada per vozidlo.
-- trip_type = firemna|sukromna (DAY-1, určuje 100/50 DPH aj krátenie výdavkov). status = proposed|active|voided.
-- (proposed = návrh z faktúry, čaká na potvrdenie; active = potvrdená; voided = stornovaná, ostáva viditeľná.)
-- corrects_id → id stornovaného záznamu (storno+nový, nikdy edit po uzávierke). JSON: stops (zastávky),
-- provenance (zdroj+diff návrh→potvrdené). odo_before/after per jazda (uložené, nie dopočítané — kaskáda pri oprave).
CREATE TABLE IF NOT EXISTS vehicle_trips (
  id TEXT PRIMARY KEY,
  created_at TEXT, updated_at TEXT, rev INTEGER NOT NULL DEFAULT 1,
  deleted_at TEXT, schema_version INTEGER NOT NULL DEFAULT 1, obligation_pack_version INTEGER NOT NULL DEFAULT 0,
  vehicle_id TEXT NOT NULL,
  seq_no INTEGER,
  driver_id TEXT, driver_name TEXT, confirmed_by TEXT,
  trip_type TEXT,
  occurred_from TEXT, occurred_to TEXT, tz_offset TEXT,
  purpose TEXT, route_from TEXT, route_to TEXT,
  km INTEGER, odo_before INTEGER, odo_after INTEGER,
  status TEXT NOT NULL DEFAULT 'active', corrects_id TEXT, void_reason TEXT,
  period_key TEXT, closed_at TEXT,
  stops TEXT, provenance TEXT, custom TEXT
);
CREATE INDEX IF NOT EXISTS idx_vtrips_vehicle ON vehicle_trips(vehicle_id);
CREATE INDEX IF NOT EXISTS idx_vtrips_seq ON vehicle_trips(vehicle_id, seq_no);
CREATE INDEX IF NOT EXISTS idx_vtrips_period ON vehicle_trips(vehicle_id, period_key);
CREATE INDEX IF NOT EXISTS idx_vtrips_status ON vehicle_trips(status);
CREATE INDEX IF NOT EXISTS idx_vtrips_deleted_at ON vehicle_trips(deleted_at);

-- 23) vehicle_trip_ledger — LOCAL-ONLY append-only HASH-CHAIN denník (dôkaz pred daňovým úradom).
-- NIKDY v SYNC_STORE_CONFIG (rovnako ako decision_events: sync pageSize=1000 bez pagination by
-- append-only store tíško osekal; sync ledgera príde segmentovaný, samostatný task).
-- entry_hash = sha256(canonical(payload bez volatile) | prev_hash) → reťaz per vozidlo (seq).
-- Písané VÝLUČNE cez vehicleLedger.appendTripEvent (put-if-absent, žiadny update/delete/prune).
-- verifyLedgerChain deteguje díeru/prepis (chýbajúci seq alebo nesediaci hash).
CREATE TABLE IF NOT EXISTS vehicle_trip_ledger (
  id TEXT PRIMARY KEY,
  vehicle_id TEXT NOT NULL,
  seq INTEGER NOT NULL,
  event_type TEXT NOT NULL,
  trip_id TEXT,
  prev_hash TEXT NOT NULL,
  entry_hash TEXT NOT NULL,
  payload TEXT,
  actor_type TEXT NOT NULL,
  actor_id_hash TEXT,
  device_id_hash TEXT NOT NULL,
  occurred_at TEXT NOT NULL,
  created_at TEXT NOT NULL
);
CREATE UNIQUE INDEX IF NOT EXISTS idx_vtledger_seq_unique ON vehicle_trip_ledger(vehicle_id, seq);
CREATE INDEX IF NOT EXISTS idx_vtledger_vehicle ON vehicle_trip_ledger(vehicle_id);
CREATE INDEX IF NOT EXISTS idx_vtledger_trip ON vehicle_trip_ledger(trip_id);
CREATE INDEX IF NOT EXISTS idx_vtledger_occurred ON vehicle_trip_ledger(occurred_at);

-- 24) vehicle_costs — PROJEKCIA nakladov na vozidlo (UI cita odtialto). Autoritativny dokaz je
-- vehicle_trip_ledger (event cost_recorded/cost_voided/cost_corrected). Full LWW triplet + kernel
-- markery, Drive sync. Podla poradcu (FS 3/DPH/2025/MU) evidencia musi obsahovat VSETKY tovary/sluzby
-- na prevadzku vozidla (nielen PHM): typ, specifikacia, obstaravacia cena bez DPH, datum, cislo dokladu.
-- cost_type = phm|servis|pneu|parkovne|myto|poistenie|ine. deduction_pct = efektivny odpocet DPH 50/100
-- odvodeny od rezimu vozidla v den dokladu (100 len ak je vozidlo v ten den 100%-eligible; inak 50).
-- PHM special: liters + trip_id (vazba na jazdu). attachment_hash = hash zdrojoveho dokladu (zdrojova_priloha).
-- status = active|voided. corrects_id -> id stornovaneho (storno + novy, nikdy edit po uzavierke, par6).
CREATE TABLE IF NOT EXISTS vehicle_costs (
  id TEXT PRIMARY KEY,
  created_at TEXT, updated_at TEXT, rev INTEGER NOT NULL DEFAULT 1,
  deleted_at TEXT, schema_version INTEGER NOT NULL DEFAULT 1, obligation_pack_version INTEGER NOT NULL DEFAULT 0,
  vehicle_id TEXT NOT NULL,
  seq_no INTEGER,
  cost_type TEXT,
  specification TEXT,
  price_base_cents INTEGER, vat_rate INTEGER, vat_cents INTEGER,
  deduction_pct INTEGER,
  doc_number TEXT, doc_date TEXT, attachment_hash TEXT,
  trip_id TEXT, liters REAL,
  status TEXT NOT NULL DEFAULT 'active', corrects_id TEXT, void_reason TEXT,
  period_key TEXT, closed_at TEXT,
  provenance TEXT, custom TEXT
);
CREATE INDEX IF NOT EXISTS idx_vcosts_vehicle ON vehicle_costs(vehicle_id);
CREATE INDEX IF NOT EXISTS idx_vcosts_docdate ON vehicle_costs(vehicle_id, doc_date);
CREATE INDEX IF NOT EXISTS idx_vcosts_type ON vehicle_costs(cost_type);
CREATE INDEX IF NOT EXISTS idx_vcosts_status ON vehicle_costs(status);
CREATE INDEX IF NOT EXISTS idx_vcosts_deleted_at ON vehicle_costs(deleted_at);
-- 25) embeddings — LOCAL-ONLY (mimo Drive sync, ZÁMERNE NIE v SYNC_STORE_CONFIG ani PK/STORES).
-- Sémantické hľadanie: vektor významu záznamu ako BLOB (Float32 little-endian, same-device read).
-- Odvodené dáta — regenerovateľné z record/counterparty/collaborator_tasks cez embedEngine; NIKDY
-- sa nesyncujú (rôzne zariadenia môžu mať inú verziu modelu → lokálna regenerácia je SSOT vektorov).
-- Prístup je RAW SQL cez search/vectorStore.js (NIE cez adapter proxy — BLOB + cosine, žiadne LWW/JSON).
-- PK (store, record_id) = práve jeden vektor na záznam. text_hash = guard proti zbytočnému re-embedu
-- nezmeneného textu. model_ver = pri zmene modelu spustí re-index. dim = dĺžka vektora (kontrola).
CREATE TABLE IF NOT EXISTS embeddings (
  store TEXT NOT NULL,
  record_id TEXT NOT NULL,
  vector BLOB NOT NULL,
  dim INTEGER NOT NULL,
  model_ver TEXT NOT NULL,
  text_hash TEXT NOT NULL,
  updated_at TEXT NOT NULL,
  PRIMARY KEY (store, record_id)
);
CREATE INDEX IF NOT EXISTS idx_embeddings_store ON embeddings(store);
`;

// Jediný unikátny index, ktorý sa vytvára SAMOSTATNE — až po reconcile
// duplikátov (pre existujúce DB). Dôvod: ak by sme ho vytvorili spolu s DDL,
// na existujúcej DB s duplikátmi by zlyhal a boot by spadol.
export const DOC_SOURCE_UNIQUE_INDEX = `
CREATE UNIQUE INDEX IF NOT EXISTS idx_records_document_source_unique
  ON records(module, source)
  WHERE module = 'document' AND source IS NOT NULL
    AND deleted_at IS NULL AND source LIKE '%:%';
`;

// ─── Per-store PK mapa ───────────────────────────────────────────────────────
// driveSync/backup mapuje per-store PK; adapter ju potrebuje pre get/upsert.
export const PK = {
  records: 'id',
  categories: 'id',
  category_rules: 'id',
  vendors: 'ico',
  settings: 'key',
  counterparties: 'id',
  bank_transactions: 'id',
  containers: 'id',
  obligation_state: 'obligation_id',
  recommendation_state: 'recommendation_id',
  advisor_log: 'id',
  prediction_log: 'id',
  decision_events: 'id',
  commitments: 'id',
  collaborator_tasks: 'id',
  collaborator_task_secrets: 'id',
  flow_runs: 'id',
  sync_outbox: 'id',
  sync_conflicts: 'id',
  pack_migration_sessions: 'id',
  import_sessions: 'id',
  vehicles: 'id',
  vehicle_trips: 'id',
  vehicle_trip_ledger: 'id',
  vehicle_costs: 'id',
};

// ─── JSON stĺpce per-store (TEXT, stringify/parse v adapteri) ─────────────────
export const JSON_COLUMNS = {
  records: ['items', 'counterparty', 'seller', 'handshake', 'custom', 'vat', 'einvoice', 'ocr_raw', '_hlc'],
  counterparties: ['_hlc'],
  bank_transactions: ['raw', '_hlc'],
  containers: ['_hlc'],
  settings: ['value', 'tokens', 'pub', 'sec'],
  decision_events: ['record_ids', 'expected', 'context_bands'],
  sync_conflicts: ['loser_json', 'winner_json'],
  flow_runs: ['context', 'steps'],
  vehicles: ['intervals', 'custom'],
  vehicle_trips: ['stops', 'provenance', 'custom'],
  vehicle_trip_ledger: ['payload'],
  vehicle_costs: ['provenance', 'custom'],
  // vlna 3 HLC: `_hlc` JSON mapa musí byť parse/stringify inak upsert padne (object → TEXT).
  commitments: ['_hlc'],
};

// ─── Boolean stĺpce per-store (INTEGER 1/0 v SQL, true/false v appke) ─────────
// serializeRow ukladá bool ako 1/0; deserializeRow ich musí konvertovať späť,
// inak konzument s `=== true` (napr. daňový filter na tax_relevant) dostane 1.
// Zdroj: DDL vyššie — jediný natívny bool stĺpec je categories.tax_relevant
// (žiadne is_*/has_* INTEGER bool stĺpce neexistujú).
export const BOOL_COLUMNS = {
  categories: ['tax_relevant'],
  records: ['paid'], // createExpense/deriveExpensePaid píše boolean; konzument (cashflow `paid !== true`) potrebuje true/false, nie 1/0
};

export const STORES = Object.keys(PK);

// ─── applySchema(engine) — idempotentné CREATE + seed ────────────────────────
// Vola sa v initV2 po otvorení DB. CREATE TABLE IF NOT EXISTS + seed guard cez
// settings._seeded flag (one-time). Seed: 6 kategórií + 5 pravidiel.
// POZOR: dynamický import reconcileSource, aby sa zabránilo cirkulárnej
// závislosti (schema → reconcileSource → driveSync → db → adapter → schema).

export async function applySchema(engine) {
  // DDL je multi-statement → exec naraz.
  await engine.exec(DDL);

  // Additive migration for DBs created before calibrated forecast bands.
  // SQLite has no portable ADD COLUMN IF NOT EXISTS, so inspect first.
  const predictionColumns = await engine.all('PRAGMA table_info(prediction_log)', []);
  const existingPredictionColumns = new Set(predictionColumns.map((column) => column.name));
  for (const [name, type] of [
    ['predicted_p10_cents', 'INTEGER'],
    ['predicted_p90_cents', 'INTEGER'],
    ['target_from', 'TEXT'],
    ['target_to', 'TEXT'],
  ]) {
    if (!existingPredictionColumns.has(name)) {
      await engine.run(`ALTER TABLE prediction_log ADD COLUMN ${name} ${type}`, []);
    }
  }

  // Per-field CRDT (sync hardening Fáza 2): additívny `_hlc` stĺpec drží per-pole HLC mapu
  // `{_rev, fields:{col:"ts:count:node"}, deleted:...}` pre KAŽDÝ store v HLC_STORES (records +
  // vlna 2: counterparties/bank_transactions/containers). Staré DB ho dostanú cez ADD COLUMN →
  // build ho vie PRIJAŤ/uložiť/preposlať; merge padne na LWW kým riadok validný `_hlc` nemá
  // (mixed-fleet safe). Bez stĺpca by remote payload s `_hlc` zhodil put() na `no such column`.
  // Každý ALTER je idempotentný (PRAGMA check) → bezpečné aj na DB s časťou stĺpcov. Vzor: prediction_log.
  for (const table of HLC_STORES) {
    // eslint-disable-next-line no-await-in-loop
    const columns = await engine.all(`PRAGMA table_info(${table})`, []);
    if (!columns.some((column) => column.name === '_hlc')) {
      // eslint-disable-next-line no-await-in-loop
      await engine.run(`ALTER TABLE ${table} ADD COLUMN _hlc TEXT`, []);
    }
  }

  // v18 migration (SUBTRAKTÍVNA — prvá svojho druhu v tejto schéme, ostatné tu sú vždy len
  // ADD COLUMN). Existujúca lokálna DB (pred touto session) má capability_token/invite_url
  // ako NOT NULL stĺpce PRIAMO na collaborator_tasks — tá tabuľka je OD TERAZ v
  // SYNC_STORE_CONFIG, takže ponechanie starých stĺpcov by pri najbližšom sync-push poslalo
  // existujúci token na Drive (rovnaká trieda ako 2026-07-26 backup leak, tentokrát cez sync
  // namiesto zálohy — cross-family nález, Codex). SQLite nevie prenosne odstrániť NOT NULL
  // stĺpec cez ALTER → klasický SQLite rebuild (rename→create novú DDL→copy→migruj secrets→
  // drop). Beží LEN RAZ (PRAGMA guard) — po prvom behu má tabuľka už novú DDL a podmienka je
  // navždy false. Fresh v18 DB (CREATE TABLE IF NOT EXISTS vyššie ju vytvorí rovno v novom
  // tvare) tento blok preskočí — capability_token stĺpec tam nikdy neexistoval.
  // Celý rebuild v JEDNEJ tx (Codex cross-family nález 2026-08-07 kolo 2): bez toho by
  // prerušenie (pád appky/OS medzi RENAME a DROP) nechalo dáta v osirotenej
  // `collaborator_tasks_v17_migrating` — nasledujúci štart by PRAGMA guard vyhodnotil ako
  // "už migrované" (nová tabuľka nemá capability_token) a osirotenú tabuľku by už NIKDY
  // znova neskúsil domigrovať → tichá strata tokenov. BEGIN IMMEDIATE…COMMIT/ROLLBACK
  // (engine.transaction) garantuje všetko-alebo-nič.
  const collabCols = await engine.all('PRAGMA table_info(collaborator_tasks)', []);
  if (collabCols.some((c) => c.name === 'capability_token')) {
    await engine.transaction(async (tx) => tx.exec(`
      ALTER TABLE collaborator_tasks RENAME TO collaborator_tasks_v17_migrating;
      CREATE TABLE collaborator_tasks (
        id TEXT PRIMARY KEY,
        created_at TEXT, updated_at TEXT, rev INTEGER NOT NULL DEFAULT 1,
        deleted_at TEXT, schema_version INTEGER NOT NULL DEFAULT 1,
        obligation_pack_version INTEGER NOT NULL DEFAULT 0,
        title TEXT NOT NULL,
        due_date TEXT,
        status TEXT NOT NULL,
        status_observed_at TEXT
      );
      INSERT INTO collaborator_tasks (id, created_at, updated_at, rev, deleted_at, schema_version, obligation_pack_version, title, due_date, status, status_observed_at)
        SELECT id, created_at, updated_at, rev, deleted_at, schema_version, obligation_pack_version, title, due_date, status, status_observed_at
        FROM collaborator_tasks_v17_migrating;
      INSERT OR IGNORE INTO collaborator_task_secrets (id, capability_token, invite_url)
        SELECT id, capability_token, invite_url FROM collaborator_tasks_v17_migrating WHERE capability_token IS NOT NULL;
      DROP TABLE collaborator_tasks_v17_migrating;
      CREATE INDEX IF NOT EXISTS idx_collab_tasks_status ON collaborator_tasks(status);
      CREATE INDEX IF NOT EXISTS idx_collab_tasks_deleted ON collaborator_tasks(deleted_at);
    `));
  }

  // K1a additive migration: staré lokálne denníky ostávajú čitateľné, nové
  // eventy v2 dostanú striktne validovaný Band Dictionary kontext.
  const decisionEventColumns = await engine.all('PRAGMA table_info(decision_events)', []);
  const existingDecisionEventColumns = new Set(decisionEventColumns.map((column) => column.name));
  if (!existingDecisionEventColumns.has('context_bands')) {
    await engine.run('ALTER TABLE decision_events ADD COLUMN context_bands TEXT', []);
  }

  // Durable outbox triggery. INSERT aj UPDATE sú samostatné SQLite eventy.
  // Používajú kanonický SYNC_STORE_CONFIG, takže zoznam cloud storov nemá
  // druhú ručne udržiavanú kópiu v schéme.
  for (const { store, pk } of SYNC_STORE_CONFIG) {
    for (const event of ['INSERT', 'UPDATE']) {
      const suffix = event.toLowerCase();
      await engine.exec(`
        CREATE TRIGGER IF NOT EXISTS sync_outbox_${store}_${suffix}
        AFTER ${event} ON ${store}
        BEGIN
          INSERT INTO sync_outbox (id, store, pk, queued_at)
          VALUES ('${store}:' || NEW.${pk}, '${store}', CAST(NEW.${pk} AS TEXT), strftime('%Y-%m-%dT%H:%M:%fZ','now'))
          ON CONFLICT(id) DO UPDATE SET queued_at = excluded.queued_at;
        END;
      `);
    }
  }

  // Jednorazový upgrade seed: záznamy vytvorené pred trigger-outboxom musia byť
  // zaradené. Marker žije v samotnom outboxe, no nikdy sa nepokúša uploadovať.
  const outboxSeeded = await engine.get(
    "SELECT id FROM sync_outbox WHERE id = '__seeded__'",
    []
  );
  if (!outboxSeeded) {
    await engine.transaction(async (tx) => {
      for (const { store, pk } of SYNC_STORE_CONFIG) {
        await tx.run(
          `INSERT OR IGNORE INTO sync_outbox (id, store, pk, queued_at)
           SELECT ? || CAST(${pk} AS TEXT), ?, CAST(${pk} AS TEXT), ? FROM ${store}`,
          [`${store}:`, store, new Date().toISOString()]
        );
      }
      await tx.run(
        'INSERT INTO sync_outbox (id, store, pk, queued_at) VALUES (?,?,?,?)',
        ['__seeded__', '__meta__', 'v1', new Date().toISOString()]
      );
    });
  }

  // ─── Pred-vytvorenie unikátneho indexu: reconcile existujúcich duplikátov.
  // Na existujúcej DB, kde už sú dva živé document riadky s rovnakým zdrojom,
  // by CREATE UNIQUE INDEX hodil chybu. Tu najprv zlúčime kolízie podľa LWW
  // pravidla (používame rovnakú logiku ako driveSync), porazených soft-deletneme
  // a zaznamenáme do sync_conflicts. Až potom index vytvoríme — celý boot je
  // self-heal.
  const { reconcileDocumentSourceDuplicates } = await import('./reconcileSource.js');
  await reconcileDocumentSourceDuplicates(engine);
  await engine.exec(DOC_SOURCE_UNIQUE_INDEX);

  // Seed guard: idempotentný cez settings._seeded.
  const seeded = await engine.get('SELECT value FROM settings WHERE key = ?', ['_seeded']);
  if (seeded && seeded.value) return;

  const now = new Date().toISOString();
  await engine.transaction(async (tx) => {
    for (const c of SEED_CATEGORIES) {
      await tx.run(
        'INSERT OR IGNORE INTO categories (id, name_sk, tax_relevant, sort_order, updated_at) VALUES (?,?,?,?,?)',
        [c.id, c.name_sk, c.tax_relevant ? 1 : 0, c.sort_order, now]
      );
    }
    for (const r of SEED_CATEGORY_RULES) {
      await tx.run(
        'INSERT OR IGNORE INTO category_rules (id, match_type, match_value, category_id, priority) VALUES (?,?,?,?,?)',
        [r.id, r.match_type, r.match_value, r.category_id, r.priority]
      );
    }
    // Invoice counter sa NEseeduje proaktívne (chýbajúci = 0, V1 sémantika).
    await tx.run(
      'INSERT OR REPLACE INTO settings (key, value, updated_at) VALUES (?,?,?)',
      ['_seeded', JSON.stringify(true), now]
    );
  });
}
